import StreamWidget from 'global/StreamWidget';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  effects as userEffects,
  selectors as userSelectors,
} from 'lib/entities/user';
import SEO from 'global/SEO';
import { AVATAR_URL, SITE_URL } from 'constants/api';
import auth from 'utils/auth';
import { useRouter } from 'next/router';
import UserNotFound from 'global/NotFound/UserNotFound';
import QueryProvider from 'global/QueryProvider';
import ProfileHeader from './ProfileHeader';
import PinnedPost from './PinnedPost';
import { ProfileContainer } from './styled';
import DeletedUser from './DeletedUser';
import {
  PAGE_SOURCE_INDEX,
  mainProfileEvents,
} from './analytics/ProfileAnalytics';

interface UserProp {
  username: string;
}

const STATUS_DELETED = 'USER_DEACTIVATED';

function User({ username }: UserProp) {
  const { query, isReady } = useRouter();
  const source = Array.isArray(query.source) ? query.source[0] : query.source;

  const dispatch = useDispatch();
  const { detail: userDetail, isLoading, error } = useSelector(userSelectors);

  const [successGetProfile, setSuccessGetProfile] = useState<boolean>(false);

  useEffect(() => {
    // @ts-ignore;
    const isCurrentUser = auth.getUserAccess()?.username === username;
    if (isCurrentUser) {
      // @ts-ignore
      dispatch(userEffects.getCurrentUserProfile(username)).then((response) => {
        if (response?.payload?.data) {
          setSuccessGetProfile(true);
        }
      });
    } else {
      // @ts-ignore
      dispatch(userEffects.getUserProfile(username)).then((response) => {
        if (response?.payload?.data) {
          setSuccessGetProfile(true);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const clevertapId =
    typeof window !== 'undefined'
      ? window?.clevertap?.getCleverTapID()
      : undefined;

  useEffect(() => {
    const pageSource = PAGE_SOURCE_INDEX[source];
    if (isReady && pageSource && clevertapId) {
      mainProfileEvents.onUserProfileViewed(pageSource);
    }
  }, [username, isReady, clevertapId, source]);

  const loadingData = !userDetail[username];

  // Error handling
  if (!isLoading && error) {
    if (error?.error_type === STATUS_DELETED) {
      return (
        <ProfileContainer>
          <SEO title="User Not Found" />
          <DeletedUser />
        </ProfileContainer>
      );
    }

    if (!successGetProfile) {
      return (
        <>
          <SEO title="User Not Found" />
          <UserNotFound />
        </>
      );
    }
  }

  const title =
    userDetail[username] &&
    `${userDetail[username]?.profile?.fullname} (${username})`;
  const isUserBlocked = userDetail[username]?.status?.is_blocked;

  return (
    <QueryProvider>
      <ProfileContainer>
        <SEO
          title={title}
          description="Aplikasi saham untuk berdiskusi, analisa dan berinvestasi dengan harga real time, berita terkini, riset, data fundamental, charting, dan analytical tools yang lengkap."
          url={`${SITE_URL}/${username}`}
          image={`${AVATAR_URL}/${userDetail[username]?.profile?.avatar?.default}`}
        />
        <ProfileHeader user={userDetail[username]} loading={loadingData} />
        <PinnedPost username={username} />
        {userDetail[username] && (
          <StreamWidget
            type="user"
            username={username}
            noBorder
            entryPoint="user"
            isUserBlocked={isUserBlocked}
          />
        )}
      </ProfileContainer>
    </QueryProvider>
  );
}

export default User;
